const initTabbedCollection = (section) => {
  if (!section || section.dataset.tabbedCollectionInitialized === 'true') return;
  section.dataset.tabbedCollectionInitialized = 'true';

  const productArea = section.querySelector('[data-products-area]');
  const tabBehavior = section.dataset.tabBehavior || 'link';
  const defaultSortBy = section.dataset.defaultSortBy;

  const getActiveTab = () => section.querySelector('[data-collection-tab].is-active');

  const updateVariantIndicator = (card) => {
    const selectedButton = card?.querySelector('[data-variant-button].is-selected');
    const variantSelector = selectedButton?.closest('.cp-card__variants');
    if (!selectedButton || !variantSelector?.classList.contains('has-multiple')) return;

    variantSelector.style.setProperty('--selected-variant-left', `${selectedButton.offsetLeft}px`);
    variantSelector.style.setProperty('--selected-variant-width', `${selectedButton.offsetWidth}px`);
  };

  const setAddButtonState = (button, isAvailable) => {
    if (!button) return;
    button.disabled = !isAvailable;
    button.dataset.state = isAvailable ? 'idle' : 'sold-out';
    if (isAvailable) {
      button.innerHTML = `<span class="cp-card__add-label">Add to Bag</span>`;
    } else {
      button.innerHTML = `<span class="cp-card__add-label">Sold out</span>`;
    }
  };

  const updateCardVariantState = (card) => {
    updateVariantIndicator(card);
    const selectedButton = card?.querySelector('[data-variant-button].is-selected');
    const addButton = card?.querySelector('[data-add-to-cart]');
    if (selectedButton && addButton) {
      const isAvailable = selectedButton.dataset.variantAvailable === 'true';
      setAddButtonState(addButton, isAvailable);
    }
  };

  const updateVariantIndicators = () => {
    section.querySelectorAll('[data-product-card]').forEach(updateCardVariantState);
  };

  const setProductsLoading = (isLoading) => {
    if (!productArea) return;

    productArea.classList.toggle('tabbed-collection__loading', isLoading);
    productArea.setAttribute('aria-busy', isLoading ? 'true' : 'false');
  };

  const setProductsReady = () => {
    if (!productArea) return;

    productArea.classList.remove('is-initial-loading');
    setProductsLoading(false);
  };

  const setAddButtonLabel = (button) => {
    setAddButtonState(button, true);
  };

  const normalizeVariantMediaText = (value) => (value || '').trim().toLowerCase();

  const getSelectedVariantNames = (card) => {
    const selectedButton = card?.querySelector('[data-variant-button].is-selected');
    return (selectedButton?.textContent || '')
      .split('/')
      .map((option) => option.trim())
      .filter(Boolean);
  };

  const getProductImages = (card) => {
    const imagesScript = card?.querySelector('[data-product-images]');
    if (!imagesScript) return [];

    try {
      return JSON.parse(imagesScript.textContent || '[]').filter((image) => image?.src);
    } catch (error) {
      return [];
    }
  };

  const getVariantProductImages = (card) => {
    const selectedVariantNames = getSelectedVariantNames(card).map(normalizeVariantMediaText);
    const productTitle = normalizeVariantMediaText(card?.dataset.productTitle);
    const matchingImages = getProductImages(card).filter((image) => {
      const imageAlt = normalizeVariantMediaText(image.alt);
      const isSharedImage = !imageAlt || imageAlt === productTitle;
      return isSharedImage || selectedVariantNames.includes(imageAlt);
    });

    return matchingImages.length ? matchingImages : getProductImages(card);
  };

  const setProductImage = (card, productImage, nextImage) => {
    if (!card || !productImage || !nextImage?.src) return;

    productImage.src = nextImage.src;
    productImage.removeAttribute('srcset');
    productImage.alt = nextImage.alt || productImage.alt;

    const imagesScript = card.querySelector('[data-product-images]');
    if (!imagesScript) return;

    try {
      const productImages = JSON.parse(imagesScript.textContent || '[]').filter((image) => image?.src);
      const matchingImageIndex = productImages.findIndex((image) => image.src === nextImage.src);
      if (matchingImageIndex >= 0) card.dataset.imageIndex = String(matchingImageIndex);
    } catch (error) {
      // Keep the selected variant image visible even if gallery metadata is unavailable.
    }
  };

  const updateProductImageForVariant = (variantButton, card) => {
    const productImage = card?.querySelector('[data-cp-gallery-image]');
    if (!productImage || !variantButton?.dataset.variantImageSrc) return;

    setProductImage(card, productImage, {
      src: variantButton.dataset.variantImageSrc,
      alt: variantButton.dataset.variantImageAlt,
    });
  };

  const showProductImage = (button) => {
    const card = button.closest('[data-product-card]');
    const productImage = card?.querySelector('[data-cp-gallery-image]');
    const imagesScript = card?.querySelector('[data-product-images]');
    if (!card || !productImage || !imagesScript) return;

    const productImages = getVariantProductImages(card);

    if (productImages.length < 2) return;

    const direction = Number(button.dataset.imageDir || 1);
    const currentSrc = productImage.getAttribute('src') || '';
    const currentIndex = Math.max(
      productImages.findIndex((image) => image.src === currentSrc),
      0
    );
    const nextIndex = (currentIndex + direction + productImages.length) % productImages.length;
    const nextImage = productImages[nextIndex];

    setProductImage(card, productImage, nextImage);
  };

  const getSectionUrl = (collectionUrl, params = new URLSearchParams(window.location.search)) => {
    const url = new URL(collectionUrl, window.location.origin);
    const sortBy = params.get('sort_by');
    const page = params.get('page');

    if (sortBy) url.searchParams.set('sort_by', sortBy);
    if (page) url.searchParams.set('page', page);
    url.searchParams.set('section_id', section.dataset.sectionId);

    return url;
  };

  const updateActiveTab = (collectionUrl) => {
    section.querySelectorAll('[data-collection-tab]').forEach((tab) => {
      const isActive = tab.dataset.collectionUrl === collectionUrl;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) tab.setAttribute('aria-current', 'page');
      else tab.removeAttribute('aria-current');
    });
  };

  const applyPreferredDefaultSort = (params) => {
    if (!defaultSortBy || params.has('sort_by')) return false;

    params.set('sort_by', defaultSortBy);
    return true;
  };

  const replaceProducts = async (collectionUrl, params = new URLSearchParams(window.location.search), pushState = true, options = {}) => {
    if (!productArea) return;
    setProductsLoading(true);

    try {
      const response = await fetch(getSectionUrl(collectionUrl, params).toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
      if (!response.ok) throw new Error('Unable to load collection');

      const html = await response.text();
      const nextDocument = new DOMParser().parseFromString(html, 'text/html');
      const nextArea = nextDocument.querySelector('[data-products-area]');
      const nextSort = nextDocument.querySelector('[data-sort-select]');
      const currentSort = section.querySelector('[data-sort-select]');

      if (nextArea) {
        productArea.innerHTML = nextArea.innerHTML;
        productArea.dataset.activeCollectionUrl = collectionUrl;
        window.requestAnimationFrame(updateVariantIndicators);
      }

      if (nextSort && currentSort && !options.keepSortLabel) {
        currentSort.innerHTML = nextSort.innerHTML;
      }

      updateActiveTab(collectionUrl);

      if (pushState) {
        const nextUrl = new URL(collectionUrl, window.location.origin);
        const sortBy = params.get('sort_by');
        const page = params.get('page');
        if (sortBy && !options.keepSortLabel) nextUrl.searchParams.set('sort_by', sortBy);
        if (page) nextUrl.searchParams.set('page', page);
        window.history.pushState({ collectionUrl }, '', nextUrl.toString());
      }
    } catch (error) {
      window.location.href = collectionUrl;
    } finally {
      setProductsReady();
    }
  };

  const loadPreferredDefaultSort = () => {
    const params = new URLSearchParams(window.location.search);
    if (!applyPreferredDefaultSort(params)) return false;

    replaceProducts(productArea?.dataset.activeCollectionUrl || getActiveTab()?.dataset.collectionUrl || window.location.pathname, params, false, {
      keepSortLabel: true,
    });

    return true;
  };

  section.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-collection-tab]');
    if (tab && tabBehavior === 'ajax') {
      event.preventDefault();
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      const usingDefaultSort = applyPreferredDefaultSort(params);
      replaceProducts(tab.dataset.collectionUrl, params, true, { keepSortLabel: usingDefaultSort });
      return;
    }

    const paginationLink = event.target.closest('[data-pagination] a[href]');
    if (paginationLink && tabBehavior === 'ajax') {
      event.preventDefault();
      const url = new URL(paginationLink.href);
      const activeUrl = productArea?.dataset.activeCollectionUrl || getActiveTab()?.dataset.collectionUrl || window.location.pathname;
      replaceProducts(activeUrl, url.searchParams);
      window.scrollTo({ top: section.offsetTop, behavior: 'smooth' });
      return;
    }

    const imageButton = event.target.closest('[data-image-dir]');
    if (imageButton) {
      event.preventDefault();
      event.stopPropagation();
      showProductImage(imageButton);
      return;
    }

    const variantButton = event.target.closest('[data-variant-button]');
    if (variantButton) {
      const card = variantButton.closest('[data-product-card]');
      const addButton = card?.querySelector('[data-add-to-cart]');
      if (!addButton) return;

      card.querySelectorAll('[data-variant-button]').forEach((button) => {
        const isSelected = button === variantButton;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      });
      const isAvailable = variantButton.dataset.variantAvailable === 'true';
      addButton.dataset.currentVariantId = variantButton.dataset.variantId;
      setAddButtonState(addButton, isAvailable);
      const price = card.querySelector('.cp-card__price');
      if (price) price.innerHTML = variantButton.dataset.variantPrice;
      updateProductImageForVariant(variantButton, card);
      updateVariantIndicator(card);
      return;
    }

    const addButton = event.target.closest('[data-add-to-cart]');
    if (addButton) {
      addVariant(addButton);
    }
  });

  section.addEventListener('change', (event) => {
    const sortSelect = event.target.closest('[data-sort-select]');
    if (!sortSelect || !sortSelect.value) return;

    const activeUrl = productArea?.dataset.activeCollectionUrl || getActiveTab()?.dataset.collectionUrl || window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    params.set('sort_by', sortSelect.value);
    params.delete('page');

    if (tabBehavior === 'ajax') {
      replaceProducts(activeUrl, params);
    } else {
      const nextUrl = new URL(activeUrl, window.location.origin);
      nextUrl.searchParams.set('sort_by', sortSelect.value);
      window.location.href = nextUrl.toString();
    }
  });

  window.addEventListener('popstate', () => {
    if (tabBehavior !== 'ajax') return;
    const params = new URLSearchParams(window.location.search);
    const usingDefaultSort = applyPreferredDefaultSort(params);
    replaceProducts(window.location.pathname, params, false, { keepSortLabel: usingDefaultSort });
  });

  const addVariant = async (button) => {
    const variantId = button.dataset.currentVariantId;
    if (!variantId || button.disabled) return;

    const card = button.closest('[data-product-card]');
    const selectedVariantButton = card?.querySelector('[data-variant-button].is-selected');
    if (selectedVariantButton && selectedVariantButton.dataset.variantAvailable !== 'true') {
      setAddButtonState(button, false);
      return;
    }

    const previousLabel = button.innerHTML;
    button.disabled = true;
    button.dataset.state = 'adding';
    button.innerHTML = '<span class="cp-card__add-label">Adding...</span>';

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', '1');

      const cartItemsComponents = document.querySelectorAll('cart-items-component[data-section-id]');
      const sectionIds = Array.from(cartItemsComponents).map((component) => component.dataset.sectionId).filter(Boolean);
      if (sectionIds.length) formData.append('sections', sectionIds.join(','));

      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Failed to add item');

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: data,
            sourceId: section.id,
            data: {
              source: 'tabbed-collection',
              itemCount: 1,
              productId: button.dataset.productId,
              variantId,
              sections: data.sections,
            },
          },
        })
      );

      button.innerHTML = '<span class="cp-card__add-label">Added</span>';
      window.setTimeout(() => {
        const currentSelected = card?.querySelector('[data-variant-button].is-selected');
        const isCurrentAvailable = currentSelected ? currentSelected.dataset.variantAvailable === 'true' : true;
        setAddButtonState(button, isCurrentAvailable);
      }, 1200);
    } catch (error) {
      const currentSelected = card?.querySelector('[data-variant-button].is-selected');
      const isCurrentAvailable = currentSelected ? currentSelected.dataset.variantAvailable === 'true' : false;
      if (!isCurrentAvailable) {
        setAddButtonState(button, false);
      } else {
        button.innerHTML = '<span class="cp-card__add-label">Error adding</span>';
        window.setTimeout(() => {
          setAddButtonState(button, true);
        }, 1800);
      }
    }
  };

  const isLoadingPreferredSort = loadPreferredDefaultSort();
  updateVariantIndicators();

  if (!isLoadingPreferredSort) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(setProductsReady);
    });
  }
  window.addEventListener('resize', updateVariantIndicators);
};

document.querySelectorAll('[data-tabbed-collection]').forEach(initTabbedCollection);

document.addEventListener('shopify:section:load', (event) => {
  initTabbedCollection(event.target.querySelector('[data-tabbed-collection]'));
});
